import React from 'react';
import { 
  LockClosedIcon, 
  CheckCircleIcon, 
  FireIcon,
  MusicalNoteIcon,
  TrophyIcon,
  SparklesIcon
} from '@heroicons/react/24/solid';
import subscriptionService from '../services/subscriptionService';

/**
 * DashboardGate component
 * Shows a gate screen when user hasn't met requirements to unlock the monitor dashboard.
 * Displays different messages based on subscription status.
 */
const DashboardGate = ({ accessData, isSubscribed }) => {
  const { unlocked, requirements, stats } = accessData;

  // Helper to format exercise name
  const formatExerciseName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // If unlocked and subscribed, show welcome message
  if (unlocked && isSubscribed) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="max-w-2xl w-full text-center p-8 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl shadow-lg border border-purple-200">
          <div className="mb-6">
            <SparklesIcon className="h-20 w-20 text-purple-600 mx-auto mb-4 animate-pulse" />
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto -mt-8 mb-4" />
          </div>
          
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            🎉 Your Dashboard is Ready!
          </h2>
          
          <p className="text-lg text-gray-700 mb-6">
            Congratulations! You've unlocked the Practice Analytics dashboard.
          </p>

          <div className="bg-white rounded-lg p-6 shadow-md mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Your Journey So Far</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Recordings */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                <MusicalNoteIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                <div className="text-3xl font-bold text-blue-900">
                  {stats.total_sessions.toLocaleString()}
                </div>
                <div className="text-sm text-blue-700 font-medium">
                  Total Recordings
                </div>
              </div>

              {/* Favorite Exercise */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                <TrophyIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                <div className="text-3xl font-bold text-purple-900">
                  {stats.top_exercise_count}
                </div>
                <div className="text-sm text-purple-700 font-medium">
                  {formatExerciseName(stats.top_exercise)}
                </div>
              </div>

              {/* Current Streak */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                <FireIcon className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                <div className="text-3xl font-bold text-orange-900">
                  {stats.current_streak}
                </div>
                <div className="text-sm text-orange-700 font-medium">
                  Day Streak
                </div>
              </div>
            </div>
          </div>

          <p className="text-gray-600">
            See analysis across <strong>{stats.total_sessions.toLocaleString()}</strong> exercises
          </p>
        </div>
      </div>
    );
  }

  // Show screen with progress (either locked or unlocked + unsubscribed)
  return (
    <div className="flex items-center justify-center h-full p-4 bg-gray-50 overflow-hidden">
      <div className="max-w-6xl w-full p-6 bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="text-center mb-4">
          {unlocked ? (
            // Unlocked celebration for unsubscribed users
            <div>
              <div className="mb-3 flex items-center justify-center gap-2">
                <SparklesIcon className="h-12 w-12 text-green-500 animate-pulse" />
                <CheckCircleIcon className="h-10 w-10 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                🎉 Your Dashboard is Ready!
              </h2>
              <p className="text-base text-gray-700 mb-4">
                Congratulations! You've completed all requirements.
              </p>
            </div>
          ) : (
            // Locked state
            <div>
              <LockClosedIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Practice Analytics Dashboard
              </h2>
              
              {/* Different message based on subscription status */}
              {isSubscribed ? (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <p className="text-base text-purple-900 mb-1 font-semibold">
                    Thank you for subscribing! 🎉
                  </p>
                  <p className="text-sm text-purple-800">
                    Complete the requirements below to unlock your personalized analytics dashboard.
                  </p>
                </div>
              ) : (
                <p className="text-gray-600 mb-4 text-sm">
                  Complete the requirements below to unlock powerful insights into your practice sessions.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Requirements Progress */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Requirement 1: Total Sessions */}
          <div className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <MusicalNoteIcon className={`h-5 w-5 ${
                  requirements.total_sessions.met ? 'text-green-500' : 'text-gray-400'
                }`} />
                <h3 className="text-base font-semibold text-gray-900">
                  Record 1,000 Exercises
                </h3>
              </div>
              {requirements.total_sessions.met && (
                <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-gray-600 mb-2">
              Build a strong foundation of practice data
            </p>
            
            {/* Progress Bar */}
            <div className="relative">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-700 font-medium">
                  {requirements.total_sessions.current.toLocaleString()} / {requirements.total_sessions.required.toLocaleString()}
                </span>
                <span className={`font-semibold ${
                  requirements.total_sessions.met ? 'text-green-600' : 'text-indigo-600'
                }`}>
                  {requirements.total_sessions.met ? '✓' : `${requirements.total_sessions.remaining.toLocaleString()} to go`}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    requirements.total_sessions.met 
                      ? 'bg-gradient-to-r from-green-500 to-green-600' 
                      : 'bg-gradient-to-r from-indigo-500 to-indigo-600'
                  }`}
                  style={{ 
                    width: `${Math.min(100, (requirements.total_sessions.current / requirements.total_sessions.required) * 100)}%` 
                  }}
                />
              </div>
            </div>
          </div>

          {/* Requirement 2: Favorite Exercise */}
          <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <TrophyIcon className={`h-5 w-5 ${
                  requirements.favorite_exercise.met ? 'text-green-500' : 'text-gray-400'
                }`} />
                <h3 className="text-base font-semibold text-gray-900">
                  Master Your Favorite
                </h3>
              </div>
              {requirements.favorite_exercise.met && (
                <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-gray-600 mb-2">
              Record your top exercise 5 times
            </p>
            
            {/* Progress */}
            {requirements.favorite_exercise.exercise_name && (
              <div className="bg-purple-50 rounded p-2 mb-2">
                <div className="text-xs text-purple-900 font-medium truncate">
                  Top: {formatExerciseName(requirements.favorite_exercise.exercise_name)}
                </div>
              </div>
            )}
            
            <div className="relative">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-700 font-medium">
                  {requirements.favorite_exercise.current} / {requirements.favorite_exercise.required} recordings
                </span>
                <span className={`font-semibold ${
                  requirements.favorite_exercise.met ? 'text-green-600' : 'text-purple-600'
                }`}>
                  {requirements.favorite_exercise.met ? '✓' : `${requirements.favorite_exercise.remaining} to go`}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ${
                    requirements.favorite_exercise.met 
                      ? 'bg-gradient-to-r from-green-500 to-green-600' 
                      : 'bg-gradient-to-r from-purple-500 to-purple-600'
                  }`}
                  style={{ 
                    width: `${Math.min(100, (requirements.favorite_exercise.current / requirements.favorite_exercise.required) * 100)}%` 
                  }}
                />
              </div>
            </div>
          </div>

          {/* Requirement 3: Practice Streak */}
          <div className="border border-gray-200 rounded-lg p-4 hover:border-orange-300 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <FireIcon className={`h-5 w-5 ${
                  requirements.streak.met ? 'text-green-500' : 'text-gray-400'
                }`} />
                <h3 className="text-base font-semibold text-gray-900">
                  Build a 3-Day Streak
                </h3>
              </div>
              {requirements.streak.met && (
                <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
              )}
            </div>
            <p className="text-xs text-gray-600 mb-2">
              Practice on 3 consecutive days
            </p>
            
            {/* Streak Stats */}
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div className="bg-orange-50 rounded p-2 text-center">
                <div className="text-xl font-bold text-orange-900">
                  {requirements.streak.current}
                </div>
                <div className="text-xs text-orange-700 font-medium">
                  Current Streak
                </div>
              </div>
              <div className="bg-orange-50 rounded p-2 text-center">
                <div className="text-xl font-bold text-orange-900">
                  {requirements.streak.longest}
                </div>
                <div className="text-xs text-orange-700 font-medium">
                  Longest Streak
                </div>
              </div>
            </div>
            
            {!requirements.streak.met && (
              <div className="text-xs text-gray-600 text-center">
                {requirements.streak.current > 0 
                  ? `${3 - requirements.streak.current} more day${3 - requirements.streak.current === 1 ? '' : 's'} to go!`
                  : 'Start practicing today to begin your streak!'
                }
              </div>
            )}
          </div>
        </div>

        {/* Call to Action */}
        {!isSubscribed && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {unlocked ? 'Unlock Even More Features!' : 'Want to unlock even more features?'}
                </h3>
                <p className="text-sm text-gray-700">
                  {unlocked 
                    ? 'Subscribe now to unlock advanced analytics and additional insights.'
                    : 'Subscribe to get additional analytics and insights once you unlock the dashboard.'
                  }
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    await subscriptionService.createCheckoutSessionForPeriod('monthly');
                  } catch (error) {
                    console.error('Error creating checkout session:', error);
                    alert('Failed to start checkout. Please try again.');
                  }
                }}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors shadow-md whitespace-nowrap"
              >
                Subscribe Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardGate;

